import { Box, Button, Flex, HStack, Tag, Text, VStack } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { formatDate, modifiedUrl, relativeUrl } from "../../../../utils";
import { labellerTitleForType } from "./AnswerDetailActivity";
import { LabelText, ValueText } from "components/text/TextTypes";
import { ArrowRight, Paperclip, FilePdf, FileDoc, FileXls, FileCsv, FilePpt, FileText, GlobeSimple, File } from "@phosphor-icons/react";
import LocalSpinner from "components/LocalSpinner";
import { useQuery } from "react-query";
import { queryKey } from "api/api-utils";
import fileinfoService from "api/common/fileinfo.service";
import { getFileType } from "api/common/fileinfo";
import FileDownloadButton from "components/FileDownloadButton";
import NonEditTextArea from "../../common-components/NonEditTextArea";
import AvatarBadge from "./AvatarBadge";
import { useRightSidebar } from "../../../../context/RightSidebarContext";
import { StatusTag } from "./AnswerDetailActivity";
import useAirUserRoles from "../../../../utils/AuthUtils";
import { useEffect, useState, useMemo } from "react";
import { AIFeedbackThumbsUpDown, AIFeedbackComment } from "pages/ISSB/components/AIAssistPanel/AIFeedbackControls";
import { RichTextViewer } from "./RichTextEditor/RichTextEditor";
import { foundationSourceRows, sourceSlicesOf } from "../activitySourceKinds";
import { multipartAnswerToString, isMultipartQuestion } from "../helper";
import { dedupe } from "utils";
import QuestionPartsList from "../QuestionPartsList";
import { apiGetThread } from "api/ai/ai.service";
import { markdownToHtml } from "../../../../utils/text-utils";
import CitationSidePanel from "../../CitationSidePanel";
import useOpenCitation from "../useOpenCitation";
/**
 * @param {!string} status "completeness" or "confidence"
 * @param {!Object} activity
 * @returns {?string}
 */
export function getStatusFromActivity(status, activity) {
  const jsonPatch = activity.jsonPatch?.filter((patch) => patch.path === "/" + status);
  return jsonPatch?.map((patch) => patch.value)[0];
}

/**
 * TODO translation and plural handling
 */
function t8n(str) {
  return str;
}

/**
 * Figma design:
 *
 * AI draft:
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=2863-7258&t=1acjghfnLW3GK2wJ-0
 *
 * Inbox/Detail view
 * https://www.figma.com/board/47Oms7MiOVhcVw7SxahOtN/FINTECH-WORKSHOP-DEMO?node-id=0-7052&t=cU0ikWuVCkyYSNrw-0
 * https://www.figma.com/board/47Oms7MiOVhcVw7SxahOtN/FINTECH-WORKSHOP-DEMO?node-id=0-7131&t=cU0ikWuVCkyYSNrw-0
 * Older? design:
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=121-12580&t=leB44EcuC9e1asfF-0
 *
 */
const AnswerActivityOneEvent = ({ activity, applyActivity, questionId, canSave, setActivityId, questionData }) => {
  console.log("AnswerActivityOneEvent", activity);
  const { setSize } = useRightSidebar();
  useEffect(() => {
    setSize("lg");
    return () => {
      setSize("md");
    };
  }, [setSize]);

  const handleApply = () => {
    // Defined in AnswerDetailActivity.jsx. This sets the answer + confidence + completeness. Not files.
    // Note: See also applyActivity events for doing similar for apply-from-chat
    applyActivity(activity);
  };

  return (
    <VStack spacing={6} align="stretch" my={4}>
      {/* Title */}
      <Flex direction="row" gap={2}>
        <AvatarBadge name={activity.createdBy} ai={activity.byAI} />
        {activity.draft && <i> draft</i>}
        <Text fontSize="heading.s" fontWeight="bold" color="darkTeal">
          {labellerTitleForType(activity)}
          {activity.type === "CHANGE_FIELD" && <span> {activity.field}</span>}
        </Text>
      </Flex>
      {/* User and Date */}
      <div>
        {" "}
        {/* NB: use div wrappers to manage the spacing: Label&Text close, then flex gaps between*/}
        <LabelText color="headingGrey" fontSize="body.xs">
          Date
        </LabelText>
        <ValueText>{formatDate(activity?.date, true, false)}</ValueText>
      </div>
      {activity.createdBy && (
        <div>
          <LabelText color="headingGrey" fontSize="body.xs">
            Created by
          </LabelText>
          <ValueText>{activity.createdBy}</ValueText>
        </div>
      )}

      {activity?.type === "UPDATE_ANSWER" && (
        <ActivityDetailsUpdateAnswer activity={activity} questionData={questionData} />
      )}

      {activity?.type === "CHANGE_OWNER" && (
        <Flex align="center" gap={2} mt={2}>
          <Tag bg="pink" color="black" px={3} py={1} borderRadius="md">
            {activity?.oldValue}
          </Tag>
          <ArrowRight size={16} />
          <Tag bg="yellow.100" color="black" px={3} py={1} borderRadius="md">
            {activity?.newValue}
          </Tag>
        </Flex>
      )}

      {activity?.type === "CHANGE_STAGE" && (
        <Flex align="center" gap={2} mt={2}>
          <Tag bg="pink" color="black" px={3} py={1} borderRadius="md">
            {activity?.oldValue}
          </Tag>
          <ArrowRight size={16} />
          <Tag bg="yellow.100" color="black" px={3} py={1} borderRadius="md">
            {activity?.newValue}
          </Tag>
        </Flex>
      )}

      {activity?.type === "ASSIGN_ASSIGNEE" && (
        <Box>
          <StatusTag field="assignee" value={activity?.newValue} />
        </Box>
      )}

      {activity?.type === "REMOVE_ASSIGNEE" && (
        <Box>
          <Tag bg="pink" color="black" mt={2}>
            {activity?.oldValue}
          </Tag>
        </Box>
      )}

      {activity?.type === "CHANGE_FIELD" && (
        <Flex align="center" gap={2} mt={2}>
          <Tag bg="pink" color="black" px={3} py={1} borderRadius="md">
            {activity?.oldValue}
          </Tag>
          <ArrowRight size={16} />
          <Tag bg="yellow.100" color="black" px={3} py={1} borderRadius="md">
            {activity?.newValue}
          </Tag>
        </Flex>
      )}

      <AICheckOutput activity={activity} />

      <ActivityDetailsSources activity={activity} questionData={questionData} />

      <Flex direction="row" gap={2} justify="space-around">
        <Button
          style={{ width: "40%" }}
          variant="secondary"
          onClick={(e) => {
            setSize("md");
            // navigate(relativeUrl(modifiedUrl({ activityId: null })));
            setActivityId(null);
          }}
        >
          Exit
        </Button>
        {activity.type === "UPDATE_ANSWER" &&
          canSave &&
          /* no apply button for no-answer-found */ (activity.aiSuccess || !activity.byAI || !activity.draft) && (
            <Button style={{ width: "40%" }} variant="primary" onClick={handleApply}>
              {activity.draft ? "Apply Answer" : "Reapply Answer"}
            </Button>
          )}
      </Flex>
    </VStack>
  );
};

export function AICheckOutput({ activity }) {
  // June 2025 HACK: factcheck issues are only on the Thread - they arent logged on the activity if the answer fails due to factcheck. This should be fixed in future data
  let checkerIssue = null;
  let [threadId, messageId, traceId] = activity.aiId?.split(":") || [null, null, null];
  const { data: aiThread } = useQuery(queryKey("aiThread", threadId), () => apiGetThread(threadId), {
    enabled: !!threadId && activity.aiSuccess === false,
  });
  if (aiThread?.messages) {
    const aiMessage = aiThread.messages.find((m) => m.output_id === messageId);
    checkerIssue = aiMessage?.json_content?.factcheck?.issue;
    // console.log("checkerIssue", checkerIssue, "from", aiMessage, "aiThread", aiThread);
    if (!checkerIssue) {
      // for if the AI ended with a type:data_request message
      checkerIssue = aiMessage?.json_content?.request;
    }
  }
  // end June 2025 HACK
  // maybe merge `reason`, `critique` into `comment`??
  let text = activity.reason || activity.critique || checkerIssue;
  if (!text) return null;
  return (
    <div>
      <LabelText>{activity.reason ? "Reason" : "AI Check"}</LabelText>
      <ValueText>{text}</ValueText>
    </div>
  );
}

/**
 * Sources: files attached to the answer, or used in writing the answer
 * + pages
 */
export function ActivityDetailsSources({ activity, questionData }) {
  // Did it specify files? Either in the json-patch diff, the filesUsed field, or sourceSlices
  const patchFiles = activity.jsonPatch?.find((patch) => patch.path === "/files");
  let fileinfoIds = patchFiles?.value;
  fileinfoIds = (fileinfoIds || []).concat(activity.filesUsed || []);
  // One read of the patch value for all three kinds of source (was read twice, for two of them).
  const sourceSlices = sourceSlicesOf(activity);
  // Also extract file IDs from sourceSlices (which may reference files not in /files or filesUsed)
  const sliceFileIds = sourceSlices.map(s => s.fileinfoId).filter(Boolean);
  fileinfoIds = [...new Set([...fileinfoIds, ...sliceFileIds])];
  // turn file ids into fileinfo objects
  const { data: fileinfosPage, isLoading: isLoadingFileInfos } = useQuery(
    queryKey("fileinfos", activity.id),
    () => fileinfoService.list({ ids: fileinfoIds }),
    { enabled: fileinfoIds?.length > 0 }
  );
  const fileinfos = fileinfosPage?.content;
  // Warning: there are also files _used_ which is separate and doesn't get attached to the answer itself
  const { data: fileinfosUsedPage, isLoading: isLoadingFileInfosUsed } = useQuery(
    queryKey("fileinfosUsed", activity.id),
    () => fileinfoService.list({ ids: activity.filesUsed }),
    { enabled: activity.filesUsed?.length > 0 }
  );
  const fileinfosUsed = fileinfosUsedPage?.content;
  // sourceSlices
  let qaSourceSlices = sourceSlices;
  qaSourceSlices =qaSourceSlices?.filter(slice => slice.questionId);
  /*
    Foundation Data v2 figures: the third kind of source. A foundation entry carries neither fileinfoId nor
    questionId, so both filters above dropped it silently and an answer built only on the organisation's
    approved figures rendered no Sources section at all - see the early return below.
  */
  const foundationRows = foundationSourceRows(sourceSlices);

  const labelForFileInfo = (fileinfo) => {
    let label = fileinfo.title || fileinfo.filename;
    const slices = sourceSlices?.filter((slice) => "" + slice.fileinfoId === "" + fileinfo.id); // Note: paranoia on long/string ids
    if (slices?.length) {
      const pages = slices.map((slice) => slice.page);
      label += t8n(", page(s): " + pages.join(", "));
    }
    return label;
  };

  // Note: activity.filesUsed
  let anyFileInfos = (fileinfos || []).concat(fileinfosUsed || []);
  anyFileInfos = dedupe(anyFileInfos, "id");

    // citations
	const { setRightSidebar, doOpen, setActivationId } = useRightSidebar();
	const openSidebar = (qid, label) => {
		console.log("openSidebar", qid, label);
		setRightSidebar(<CitationSidePanel questionId={qid} label={label} />);
	  doOpen("CitationSidePanel:" + qid);
	};
	// The figure's VALUE is not on the slice (Java strips `content`), so the panel fetches the year's rows
	// and finds it. Its own activation id per (datapoint, version) - see activitySourceKinds.
	const openFoundationSidebar = (row) => {
		setRightSidebar(<CitationSidePanel foundation={row} label={row.label} />);
		doOpen(row.activationId);
	};

  if (!anyFileInfos?.length && !qaSourceSlices?.length && !foundationRows.length) {
	return null;
  }

  return (
    <Box mt={4}>
      <LabelText>Sources</LabelText>
      <Box borderTop="1px solid" borderColor="gray.100" mt={1}>
        <VStack spacing={0} align="stretch">
          {/* Above the documents, because an approved figure outranks a document that disagrees with it -
              that is how the prompt orders them, and a source list that reads the other way round invites
              the wrong reading of which one the answer rests on. */}
          {foundationRows.map((row) => (
            <HStack
              key={row.key}
              py={1.5}
              px={1}
              spacing={2}
              borderBottom="1px solid"
              borderColor="gray.100"
              cursor="pointer"
              _hover={{ bg: "gray.50" }}
              onClick={() => openFoundationSidebar(row)}
            >
              <Box flexShrink={0}>
                <Box
                  bg="lightGreen"
                  color="teal"
                  fontSize="10px"
                  fontWeight="bold"
                  borderRadius="sm"
                  px={1.5}
                  py={0.5}
                  textAlign="center"
                  minW="36px"
                  lineHeight="1.3"
                >
                  DATA
                </Box>
              </Box>
              <Text fontSize="body.xs" color="darkTeal" noOfLines={1} flex={1}>
                {row.label}
              </Text>
              {row.year && (
                <Text fontSize="body.xxs" color="gray.500" flexShrink={0}>
                  {row.year}
                </Text>
              )}
            </HStack>
          ))}
          {anyFileInfos?.map((fileinfo) => {
            const fileType = getFileType(fileinfo);
            const IconComponent = SOURCE_FILE_TYPE_ICONS[fileType] || File;
            const slices = sourceSlices?.filter((slice) => "" + slice.fileinfoId === "" + fileinfo.id);
            const pages = slices?.length ? slices.map((slice) => slice.page).join(", ") : null;
            /* eslint-disable-next-line eqeqeq */
            const isAttached = questionData?.files?.find((f) => f == fileinfo.id);
            return (
              <HStack
                key={fileinfo.id}
                py={1.5}
                px={1}
                spacing={2}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <Box flexShrink={0}>
                  <IconComponent size={22} color="#718096" />
                </Box>
                <FileDownloadButton fileInfo={fileinfo} as={Text} fontSize="body.xs" color="darkTeal" flex={1} noOfLines={1}>
                  {fileinfo.title || fileinfo.filename || fileinfo.id}
                </FileDownloadButton>
                {pages && (
                  <Text fontSize="body.xxs" color="gray.500" flexShrink={0}>
                    p. {pages}
                  </Text>
                )}
                {isAttached && (
                  <span title="Attached to question as evidence">
                    <Paperclip size={14} color="gray" />
                  </span>
                )}
              </HStack>
            );
          })}
          {qaSourceSlices?.map(slice => (
            <HStack
              key={slice.id}
              py={1.5}
              px={1}
              spacing={2}
              borderBottom="1px solid"
              borderColor="gray.100"
              cursor="pointer"
              _hover={{ bg: "gray.50" }}
              onClick={() => openSidebar(slice.questionId, "Question " + (slice.question_number || ''))}
            >
              <Box flexShrink={0}>
                <Box
                  bg="#E8EAF6"
                  color="#283593"
                  fontSize="10px"
                  fontWeight="bold"
                  borderRadius="sm"
                  px={1.5}
                  py={0.5}
                  textAlign="center"
                  minW="36px"
                  lineHeight="1.3"
                >
                  Q&A
                </Box>
              </Box>
              <Text fontSize="body.xs" color="darkTeal" noOfLines={1} flex={1}>
                Question {slice.question_number}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Box>
      {isLoadingFileInfos && <LocalSpinner />}
    </Box>
  );
}

const SOURCE_FILE_TYPE_ICONS = {
  PDF: FilePdf,
  DOCX: FileDoc,
  DOC: FileDoc,
  XLSX: FileXls,
  XLS: FileXls,
  CSV: FileCsv,
  PPTX: FilePpt,
  PPT: FilePpt,
  TXT: FileText,
  URL: GlobeSimple,
};

/**
 * Note: Doesn't include the "Apply Answer" button
 *
 * @param {Object} activity
 * @param {Object} questionData
 */
export function ActivityDetailsUpdateAnswer({
  activity,
  questionData,
  showOldAnswer = true,
  useEditorAsViewer = false,
  allQuestionDataByRef,
  tags,
  minimal = false,
}) {
  const [feedback, setFeedback] = useState();
  const [showFeedbackComment, setShowFeedbackComment] = useState();
  // citations in the drafted answer open in the sidebar, as they do in the answer editor
  const openCitation = useOpenCitation();

  const completeness = getStatusFromActivity("completeness", activity);
  const confidence = getStatusFromActivity("confidence", activity);

  const [threadId, messageId, traceId] = activity?.aiId ? activity.aiId.split(":") : [null, null, null];
  /** The feedback controls expect a message with an id, so create a dummy message with the right id
   * @type {ChatMessage} */
  const pseudoMessage = useMemo(() => {
    // Note: useMemo to avoid repeatedly triggering useEffect in the feedback controls
    return {
      id: messageId,
      trace_id: traceId,
      role: "assistant",
      content: activity.newValue,
      created_date: activity.date,
    };
  }, [messageId, traceId, activity.newValue, activity.date]);

  let oldValue = activity?.oldValue?.trim();
  let newValue = activity?.newValue?.trim();
  // issue seen june 2025 http://localhost:3000/app/issb/data-input/answer?id=510f7d3c-b6d5-4dbc-9367-ac7c11bb1021
  // // new value is blank but there is a new value?!
  // console.log("activity newValue", newValue);
  // let newValueJson;
  // hack: multipart answer?
  let answerParts = null;
  if (activity?.type === "UPDATE_ANSWER" && isMultipartQuestion(questionData)) {
    oldValue = multipartAnswerToString(oldValue);
    if (useEditorAsViewer) {
      try {
        const newValueObj = JSON.parse(newValue);
        answerParts = newValueObj;
      } catch (e) {
        console.log("(swallow) Error parsing newValue", newValue, e);
        useEditorAsViewer = false; // display as narrative text
      }
    } else {
      newValue = multipartAnswerToString(newValue);
    }
  }

  return (
    <>
      {!minimal && (completeness || confidence) && (
        <div>
          <LabelText>Status</LabelText>
          <HStack>
            {completeness && <StatusTag showField field="completeness" value={completeness} />}
            {confidence && <StatusTag showField field="confidence" value={confidence} />}
          </HStack>
        </div>
      )}
      {showOldAnswer && oldValue && (
        <div>
          <LabelText color="headingGrey" fontSize="body.xs">
            Old Answer
          </LabelText>
          <NonEditTextArea copyable={oldValue} copyContent={markdownToHtml(oldValue)}>
            <RichTextViewer
              content={markdownToHtml(oldValue) || "None"}
              onElementClick={openCitation}
              style={oldValue ? {} : { height: "3rem", minHeight: "3rem" }}
            />
          </NonEditTextArea>
        </div>
      )}
      <div>
        {!minimal && (
          <Flex justify="space-between">
            <LabelText color="headingGrey" fontSize="body.xs">
              {"New Answer"}
            </LabelText>
            <AIFeedbackThumbsUpDown
              threadId={threadId}
              message={pseudoMessage}
              feedback={feedback}
              setFeedback={setFeedback}
              setShowFeedbackComment={setShowFeedbackComment}
            />
          </Flex>
        )}
        {useEditorAsViewer && isMultipartQuestion(questionData) ? (
          <>
            <QuestionPartsList
              questionData={questionData}
              answerParts={answerParts}
              setAnswer={null}
              readOnly={true}
              canUseHtml={false}
              allQuestionDataByRef={allQuestionDataByRef}
              tags={tags}
            />
            {!minimal && (
              <Box mt={2}>
                <LabelText>AI Text Version</LabelText>
                {/* TODO maybe just show the summary for table questions? It will likely be repetitive for forms*/}
                <ValueText>{multipartAnswerToString(newValue)}</ValueText>
              </Box>
            )}
          </>
        ) : minimal ? (
          <Box whiteSpace="pre-wrap" color="gray.500">
            <RichTextViewer content={markdownToHtml(newValue)} onElementClick={openCitation} />
          </Box>
        ) : (
          <NonEditTextArea copyable={newValue} copyContent={markdownToHtml(newValue)}>
            <RichTextViewer content={markdownToHtml(newValue)} onElementClick={openCitation} />
          </NonEditTextArea>
        )}
      </div>
      {threadId && (
        <AIFeedbackComment
          threadId={threadId}
          message={pseudoMessage}
          feedback={feedback}
          setFeedback={setFeedback}
          showFeedbackComment={showFeedbackComment}
          setShowFeedbackComment={setShowFeedbackComment}
        />
      )}
    </>
  );
}

export default AnswerActivityOneEvent;
